import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CustomerMergeResponse,
  CustomerMergePreviewResponse,
  MergeCustomerPostRequestBody,
  MergeCustomerPreviewPostRequestBody,
} from '@lio/api-types';
import { useClient } from '@utils/api-client';
import { invalidateAfterCustomerCombine } from '@services/customer/customer.helpers';

// Re-export the published merge types so consumers can import them from the service
// barrel (`@services/customer`) alongside the hooks.
export type {
  CustomerMergeAffectedRecord,
  CustomerMergeBorConflict,
  CustomerMergeContactCompare,
  CustomerMergeContactOwner,
  CustomerMergeContactRecord,
  CustomerMergeEligibleQuote,
  CustomerMergeExcludedQuote,
  CustomerMergeLockedRecord,
  CustomerMergeRecordDisposition,
  CustomerMergePreviewResponse,
  CustomerMergeResponse,
  MergeCustomerPostRequestBody,
  MergeCustomerPreviewPostRequestBody,
} from '@lio/api-types';

/** Dry-run: returns the full effect of the prospective merge for the operator to review. */
export const useMergeCustomerPreview = () => {
  const client = useClient();
  const mutationFn = async (body: MergeCustomerPreviewPostRequestBody) =>
    client.post('customer/merge/preview', { json: body }).json<CustomerMergePreviewResponse>();
  const result = useMutation({ mutationFn });
  return { ...result, previewMergeCustomer: result.mutateAsync };
};

/** Executes the merge. */
export const useMergeCustomer = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const mutationFn = async (body: MergeCustomerPostRequestBody) =>
    client.post('customer/merge', { json: body }).json<CustomerMergeResponse>();
  const result = useMutation({
    mutationFn,
    // Both customers changed (survivor's contacts/quotes, loser's matchState).
    onSuccess: (data, variables) =>
      invalidateAfterCustomerCombine(
        queryClient,
        [variables.survivorCustomerId, variables.loserCustomerId],
        data.movedQuoteIds,
      ),
  });
  return { ...result, mergeCustomer: result.mutateAsync };
};
